/* istanbul ignore file */
import { READ_SECURITY_AUDIT_LOG } from 'domain/audit/shared/auditService';
import { ARTIFACTS_AUDIT_PATH, PERMISSIONS_AUDIT_PATH, ROLES_AUDIT_PATH } from 'domain/audit/AuditRouter';
import { ONE_CLICK_PATH } from 'infrastructure/security/authentication/authenticationRouting';
import isOneClickEnabled from 'infrastructure/security/authentication/OneClick/isOneClickEnabled';
import { ONE_STOP_HISTORY_PATH } from 'domain/shared/oneStop';
import { PROCESS_DEFINITION_PATH } from 'domain/gpo/cockpit/definition';
import { READ_DEFINITIONS_LIST } from 'domain/gpo/cockpit/definition/processDefinitionService';
import { READ_INSTANCES_LIST } from 'domain/gpo/cockpit/instance/processInstanceService';
import { PROCESS_INSTANCE_PATH } from 'domain/gpo/cockpit/instance';
import { LEADS_PATH } from 'domain/shared/lead';
import { READ_LEADS_LIST } from 'domain/sales/lead/shared/leadService';
import { READ_ACCOUNTS_LIST } from 'domain/sales/account';
import { CONTACTS_PATH } from 'domain/sales/contact/contactRouting';
import { READ_CONTACTS_LIST } from 'domain/sales/contact/shared/contactService';
import { READ_OPPORTUNITIES_LIST } from 'domain/sales/salesService';
import {
  EMPLOYEE_READ_NAME,
  GET_EMPLOYEES_LIST,
  READ_EMPLOYEE_DASHBOARD,
  EMPLOYEE_PORTAL_PATH,
  WORKERS_PATH,
} from 'domain/shared/employee';
import { PERFORMANCE_MEASUREMENT_GET } from 'domain/shared/performanceMeasurement';
import { GET_ALL_CONTRACTS_LIST } from 'domain/people/contracts/shared/contractsService';
import { READ_CANDIDATES_LIST } from 'domain/shared/candidate';
import { READ_PAYROLL_REPORT } from 'domain/finance/payroll/shared/payrollService';
import { getPayrollListUrl } from 'domain/finance/payroll/route';
import { getWorkingHoursListUrl } from 'domain/finance/workingHours/route';
import { READ_WORKING_HOURS_REPORT } from 'domain/finance/workingHours/shared/workingHoursService';
import { READ_VACANCIES_LIST } from 'domain/recruiting/vacancy/shared/vacancyService';
import {
  ASSIGNMENT_PATH,
  CREATE_ASSIGNMENT,
  READ_ASSIGNMENTS_LIST,
  VIEW_ASSIGNMENTS_TAB,
} from 'domain/shared/assignment';
import {
  PERFORMANCE_REPORTS_PATH,
  READ_PERFORMANCE_REPORT,
} from 'domain/people/performanceReport/usePerformanceReportService';
import {
  CRM_PATH,
  OPPORTUNITY_PATH,
  HANDBOOK_PATH,
  STAFFING_DASHBOARD_PATH,
  OPPORTUNITY_DASHBOARD_PATH,
} from 'domain/shared/core/sharedRouteConstants';
import { READ_SKILL_BASED_QUEUES_LIST } from 'domain/gpo/skillBasedQueue/shared/skillBasedQueueService';
import { SKILL_BASED_QUEUE_PATH } from 'domain/gpo/skillBasedQueue/skillBasedQueueRoute';
import PeopleIcon from '@mui/icons-material/PeopleOutlined';
import OneStopIcon from '@mui/icons-material/InsertDriveFileOutlined';
import BudgetAccountingIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import TopicOutlinedIcon from '@mui/icons-material/TopicOutlined';
import ProjectsIcon from '@mui/icons-material/DashboardOutlined';
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined';
import CrmIcon from '@mui/icons-material/ContactPageOutlined';
import ArtifactStorageIcon from '@mui/icons-material/SourceOutlined';
import { READ_SERVICE_CATALOG_LIST, SERVICE_CATALOG_PATH } from 'domain/shared/serviceCatalog';
import { Features, featureToggles } from 'infrastructure/featureToggle';
import { getCostAccountingListUrl } from 'domain/finance/costAccounting/route';
import { READ_COST_CENTER_REPORT } from 'domain/finance/costAccounting/shared/costAccountingService';
import { READ_LEDGER_LIST } from 'domain/finance/ledger/useLedgerService';
import { getLedgerListUrl } from 'domain/shared/ledger';
import { READ_JOURNAL_ENTRY } from 'domain/finance/journalEntry/useJournalEntryService';
import { getJournalEntryListUrl } from 'domain/finance/journalEntry/shared/useJournalEntryRouting';
import { INVOICE_PATH, READ_INVOICE_LIST } from 'domain/finance/invoice/useInvoiceService';
import { ASSET_PATH, READ_ASSET_LIST } from 'domain/finance/assets/useAssetService';
import { getExpenseListUrl, READ_EXPENSE_LIST } from 'domain/shared/expense';
import { PURCHASE_ORDER_PATH, READ_PURCHASE_ORDER_LIST } from 'domain/finance/purchaseOrder/usePurchaseOrderService';
import { READ_PAYROLL_REPORTS } from 'domain/finance/payroll/generatedPayrolls/useGeneratedPayrollsService';
import { getGeneratedPayrollsListUrl } from 'domain/finance/payroll/generatedPayrolls/route';
import { GET_ACCOUNTING_CYCLE } from 'domain/finance/costAccounting/accountingCycle/useAccountingCycle';
import { getAccountingCycleListUrl } from 'domain/finance/costAccounting/accountingCycle/shared/useAccountingCycleRouting';
import { HIRING_REQUEST_READ } from 'domain/shared/newVacancy';
import { EMPLOYEE_READ_ONE_STOP } from 'domain/gpo/oneStop/OneStopRouter';
import { VACANCY_GROUP_PATH } from 'domain/recruiting/newVacancy/routing';
import { READ_RECRUITING_DASHBOARD, RECRUITING_DASHBOARD_READ } from 'domain/shared/newVacancy';
import {
  KERNEL_SALARY_FORKS_VIEW_READ,
  SALARY_FORKS_VIEW_READ,
} from 'domain/recruiting/salaryFork/useSalaryForkService';
import { SALARY_FORKS_PATH } from 'domain/recruiting/salaryFork/routing';
import { WORKERS_DASHBOARD_PATH } from 'domain/people/employee/dashboard/constants';
import { POSITION_PROFILE_LIST_READ, POSITION_PROFILES_PATH } from 'domain/people/positionProfiles/constants';
import {
  POSITION_FILL_REQUEST_SEARCH,
  POSITION_FILL_REQUESTS_PATH,
} from 'domain/recruiting/positionFillRequests/constants';
import { WORKFORCE_ANALYTICS_PATH } from 'domain/people/workforceAnalytics/constants';
import { getVendorsListUrl } from 'domain/finance/vendors/routes';
import { QUALIFICATION_STRUCTURE_PATH } from 'domain/people/employee/qualificationStructure/routing';
import { BENCH_PATH } from 'domain/staffing/assignment/bench/routing';
import { FINANCE_VENDOR_LIST_READ } from 'domain/finance/vendors/constants';
import { MEASUREMENT_PATH } from 'domain/people/employee/performanceMeasurement/PerformanceMeasurementRouter';
import { COMPLIANCE_REPORT_PATH } from 'domain/compliance/complianceReport/router';
import { EMPLOYEE_SCORE_PATH } from 'domain/people/employee/employeeScore/EmployeeScoreRouter';
import { LEAD_REPORT_PERMISSION } from 'domain/sales/report/useLeadReportService';
import { LEAD_REPORT_PATH } from 'domain/sales/report/routing';
import { NEW_PROJECT_PATH, READ_LIST } from 'domain/shared/newProject';
import { NavigationItemType } from 'framework/navigation';
import {
  RecruitmentIcon,
  EmpoyeeBadge,
  ServicesIcon,
  SuppliersIcon,
  InventoryIcon,
  ProcessesIcon,
} from 'framework/ui/mui-builder';
import { PROCESS_ASSETS_PATH } from 'domain/gpo/processAssets/router';
import { READ_PROCESS_ASSETS } from 'domain/gpo/processAssets/constants';
import { VIEW_STAFFING_DASHBOARD } from 'domain/staffing/dashboard/constants';
import { filterTruthy } from 'framework/utils';
import { EMPLOYEE_PORTAL_ROUTE_PATHS } from 'domain/people/employee/portal/constants';
import { getProjectListUrl, READ_PROJECTS_LIST } from 'domain/shared/project';
import { ACCOUNT_PATH } from 'domain/shared/account';
import { ONE_STOP_PATH } from 'domain/shared/oneStop';
import { READ_COMPLIANCE_REGISTRY_DOCUMENTS, UPLOAD_COMPLIANCE_REGISTRY_DOCUMENTS } from 'domain/shared/compliance';
import { COMPLIANCE_REGISTRY_PATH } from 'domain/compliance/complianceRegistry/router';
import { ARTIFACT_STORAGE_PATH, FE_VIEW_ARTIFACT_STORAGE } from 'domain/shared/artifactStorage';
import { LEAVES_LIST_SEARCH } from 'domain/shared/leave/constants';
import { QUALIFICATION_STRUCTURE_LIST_READ } from 'domain/people/employee/qualificationStructure/services/qualificationStructureService';
import { SYBERRY_TODAY_PATH } from 'domain/people/syberryToday/SyberryTodayRouter';
import { PROJECTS_COMPLIACNE, PROJECTS_COMPLIANCE_LIST } from 'domain/projects/projectsCompliance/router';
import { TASK_CONSTRUCTOR_LIST_READ, TASK_CONSTRUCTOR_PATH } from '../domain/gpo/taskConstructor/constants';
import { RECRUITMENT_POOL_PATH, RECRUITMENT_POOL_SEARCH } from 'domain/recruiting/recruitmentPool/constants';
import { POSITION_SLOTS_PATH } from 'domain/shared/positionSlot';
import { POSITION_SLOT_LIST_READ } from 'domain/people/positionSlots/preview/constants';
import { BUISNESS_FUNCTION_PATH } from 'domain/organization/buisnessFunction/routing';

const PEOPLE_PATH = '/people';
const TEAMS_PATH = '/teams';

const assignmentSidebarEnabled = Boolean(featureToggles[Features.ASSIGNMENT_SIDEBAR]);
const projectDashboardEnabled = Boolean(featureToggles[Features.NEW_PROJECT_DASHBOARD]);
const teamEnabled = Boolean(featureToggles[Features.STAFFING_DASHBOARD]);
const oppDashboardEnabled = Boolean(featureToggles[Features.OPPORTUNITY_DASHBOARD]);
const operationalAssetsEnabled = Boolean(featureToggles[Features.OPERATIONAL_ASSETS]);
const taskConstructorEnabled = Boolean(featureToggles[Features.TASK_CONSTRUCTOR]);
const employeePortalComplianceEnabled = Boolean(featureToggles[Features.EMPLOYEE_PORTAL_COMPLIANCE]);
const positionSlotsEnabled = Boolean(featureToggles[Features.POSITION_SLOTS]);
const positionProfilesEnabled = Boolean(featureToggles[Features.POSITION_PROFILES]);
const positionFillRequestsEnabled = Boolean(featureToggles[Features.POSITION_FILL_REQUESTS_COLLECTION]);
const recruitmentPoolEnabled = Boolean(featureToggles[Features.RECRUITMENT_POOL]);
const workforceAnalyticsEnabled = positionSlotsEnabled && Boolean(featureToggles[Features.WORKFORCE_ANALYTICS]);
const syberryTodayEnabled = Boolean(featureToggles[Features.SYBERRY_TODAY]);
const projectsComplianceEnabled = Boolean(featureToggles[Features.PROJECTS_COMPLIANCE]);
const buisnessFunctionsEnabled = Boolean(featureToggles[Features.BUISNESS_FUNCTIONS]);
const newRecruitingTabs = Boolean(featureToggles[Features.NEW_RECRUITING_TABS]);

const asNavigationChildren = (items: Array<NavigationItemType | false | null | undefined>): NavigationItemType[] =>
  items.filter(filterTruthy);

const oneClick = isOneClickEnabled()
  ? [
      {
        name: 'One Click Sign In',
        url: ONE_CLICK_PATH,
        permissions: null,
      },
    ]
  : [];

const syberryToday = [
  {
    name: 'Syberry Today',
    url: syberryTodayEnabled ? SYBERRY_TODAY_PATH : ONE_STOP_PATH,
    shouldUseCurrentName: true,
    icon: <OneStopIcon />,
    permissions: null,
    children: asNavigationChildren([
      syberryTodayEnabled && {
        name: 'Syberry Today',
        url: SYBERRY_TODAY_PATH,
        permissions: null,
        isPageMenu: true,
      },
      {
        name: 'One Stop',
        url: ONE_STOP_PATH,
        permissions: [EMPLOYEE_READ_ONE_STOP],
        isPageMenu: true,
        children: asNavigationChildren([
          {
            name: 'Requests',
            url: ONE_STOP_PATH,
            permissions: [EMPLOYEE_READ_ONE_STOP],
            isPageMenu: true,
          },
          {
            name: 'One Stop history',
            url: ONE_STOP_HISTORY_PATH,
            permissions: [EMPLOYEE_READ_ONE_STOP],
            isPageMenu: true,
          },
        ]),
      },
      {
        name: 'Handbook',
        url: HANDBOOK_PATH,
        permissions: null,
        isPageMenu: true,
      },

      {
        name: 'My Portal',
        url: EMPLOYEE_PORTAL_PATH,
        isPageMenu: true,
        exact: true,
        includeUrls: [
          `${EMPLOYEE_PORTAL_PATH}/${EMPLOYEE_PORTAL_ROUTE_PATHS.DOCUMENTS}`,
          `${EMPLOYEE_PORTAL_PATH}/${EMPLOYEE_PORTAL_ROUTE_PATHS.POLICY_SIGN}`,
        ],
        permissions: [EMPLOYEE_READ_NAME],
      },

      employeePortalComplianceEnabled && {
        name: 'Security & Compliance',
        url: `${EMPLOYEE_PORTAL_PATH}/${EMPLOYEE_PORTAL_ROUTE_PATHS.COMPLIANCE}`,
        isPageMenu: true,
        exact: true,
        permissions: [READ_COMPLIANCE_REGISTRY_DOCUMENTS],
      },
    ]),
  },
];

const artifactStorage = [
  {
    name: 'Documents',
    url: ARTIFACT_STORAGE_PATH,
    icon: <ArtifactStorageIcon />,
    permissions: [FE_VIEW_ARTIFACT_STORAGE],
  },
];

const organization = buisnessFunctionsEnabled
  ? [
      {
        name: 'Organization',
        url: BUISNESS_FUNCTION_PATH,
        icon: <TopicOutlinedIcon />,
        shouldUseCurrentName: true,
        // TODO Update permissions for organization section
        permissions: [FE_VIEW_ARTIFACT_STORAGE],
        children: asNavigationChildren([
          buisnessFunctionsEnabled && {
            name: 'Functions',
            url: BUISNESS_FUNCTION_PATH,
            // TODO Update permissions
            permissions: [FE_VIEW_ARTIFACT_STORAGE],
            isPageMenu: true,
          },
        ]),
      },
    ]
  : [];

const recruitment = newRecruitingTabs
  ? [
      {
        name: 'Recruitment',
        shouldUseCurrentName: true,
        icon: <RecruitmentIcon />,
        url: `${VACANCY_GROUP_PATH}/new-dashboard`,
        permissions: [
          READ_CANDIDATES_LIST,
          HIRING_REQUEST_READ,
          READ_VACANCIES_LIST,
          READ_RECRUITING_DASHBOARD,
          SALARY_FORKS_VIEW_READ,
          ...(positionFillRequestsEnabled || recruitmentPoolEnabled
            ? [POSITION_FILL_REQUEST_SEARCH, RECRUITMENT_POOL_SEARCH]
            : []),
        ],
        children: asNavigationChildren([
          {
            name: 'Data',
            url: `${VACANCY_GROUP_PATH}/new-dashboard`,
            shouldUseCurrentName: true,
            permissions: null,
            isPageMenu: true,
            thirdLevelMenuChildren: asNavigationChildren([
              {
                name: 'Dashboard',
                url: `${VACANCY_GROUP_PATH}/new-dashboard`,
                permissions: [READ_RECRUITING_DASHBOARD],
                isPageMenu: true,
              },
              positionFillRequestsEnabled && {
                name: 'PFR',
                url: POSITION_FILL_REQUESTS_PATH,
                permissions: [POSITION_FILL_REQUEST_SEARCH],
                isPageMenu: true,
              },
              recruitmentPoolEnabled && {
                name: 'Recruitment Pools',
                url: RECRUITMENT_POOL_PATH,
                permissions: [RECRUITMENT_POOL_SEARCH],
                isPageMenu: true,
              },
              {
                name: 'Salary Ranges',
                url: SALARY_FORKS_PATH,
                permissions: [KERNEL_SALARY_FORKS_VIEW_READ],
                isPageMenu: true,
              },
            ]),
          },
          {
            name: 'Settings',
            url: '/recruitment-settings/roles',
            shouldUseCurrentName: true,
            permissions: null,
            isPageMenu: true,
            // Placeholder routes until recruitment settings pages are available.
            thirdLevelMenuChildren: [
              { name: 'Roles', url: '/recruitment-settings/roles', permissions: null, isPageMenu: true },
              { name: 'Assignments', url: '/recruitment-settings/assignments', permissions: null, isPageMenu: true },
              { name: 'Services', url: '/recruitment-settings/services', permissions: null, isPageMenu: true },
            ],
          },

          {
            name: 'Recruitment',
            url: '/recruitment-settings/recruitment',
            shouldUseCurrentName: true,
            permissions: null,
            isPageMenu: true,
            // Placeholder routes until recruitment settings pages are available.
            thirdLevelMenuChildren: [
              { name: 'Roles', url: '/recruitment-settings/recruitment', permissions: null, isPageMenu: true },
              { name: 'Assignments', url: '/recruitment-settings/recruitment', permissions: null, isPageMenu: true },
              { name: 'Services', url: '/recruitment-settings/recruitment', permissions: null, isPageMenu: true },
            ],
          },

          {
            name: 'Assignments',
            url: '/recruitment-settings/Assignments',
            shouldUseCurrentName: true,
            permissions: null,
            isPageMenu: true,
            // Placeholder routes until recruitment settings pages are available.
            thirdLevelMenuChildren: [
              { name: 'Roles', url: '/recruitment-settings/Assignments', permissions: null, isPageMenu: true },
              { name: 'Assignments', url: '/recruitment-settings/Assignments', permissions: null, isPageMenu: true },
              { name: 'Services', url: '/recruitment-settings/Assignments', permissions: null, isPageMenu: true },
            ],
          },
        ]),
      },
    ]
  : [
      {
        name: 'Recruitment',
        shouldUseCurrentName: true,
        icon: <RecruitmentIcon />,
        url: `${VACANCY_GROUP_PATH}/new-dashboard`,
        permissions: [
          READ_CANDIDATES_LIST,
          HIRING_REQUEST_READ,
          READ_VACANCIES_LIST,
          READ_RECRUITING_DASHBOARD,
          SALARY_FORKS_VIEW_READ,
          ...(positionFillRequestsEnabled || recruitmentPoolEnabled
            ? [POSITION_FILL_REQUEST_SEARCH, RECRUITMENT_POOL_SEARCH]
            : []),
        ],
        children: asNavigationChildren([
          {
            name: 'Dashboard',
            url: `${VACANCY_GROUP_PATH}/new-dashboard`,
            permissions: [READ_RECRUITING_DASHBOARD],
            isPageMenu: true,
          },
          positionFillRequestsEnabled && {
            name: 'PFR',
            url: POSITION_FILL_REQUESTS_PATH,
            permissions: [POSITION_FILL_REQUEST_SEARCH],
            isPageMenu: true,
          },
          recruitmentPoolEnabled && {
            name: 'Recruitment Pools',
            url: RECRUITMENT_POOL_PATH,
            permissions: [RECRUITMENT_POOL_SEARCH],
            isPageMenu: true,
          },
          {
            name: 'Salary Ranges',
            url: SALARY_FORKS_PATH,
            permissions: [KERNEL_SALARY_FORKS_VIEW_READ],
            isPageMenu: true,
          },
        ]),
      },
    ];
// Have delete items in children
const people: NavigationItemType[] = [
  {
    name: 'People',
    url: WORKERS_DASHBOARD_PATH,
    shouldUseCurrentName: true,
    icon: <EmpoyeeBadge />,
    exceptionUrls: [EMPLOYEE_PORTAL_PATH, ARTIFACT_STORAGE_PATH],
    permissions: [
      GET_EMPLOYEES_LIST,
      GET_ALL_CONTRACTS_LIST,
      LEAVES_LIST_SEARCH,
      READ_ASSIGNMENTS_LIST,
      READ_PERFORMANCE_REPORT,
      RECRUITING_DASHBOARD_READ,
      SALARY_FORKS_VIEW_READ,
      READ_EMPLOYEE_DASHBOARD,
      READ_SKILL_BASED_QUEUES_LIST,
      ...(positionSlotsEnabled || positionProfilesEnabled || workforceAnalyticsEnabled
        ? [POSITION_SLOT_LIST_READ, POSITION_PROFILE_LIST_READ]
        : []),
    ],
    children: asNavigationChildren([
      {
        name: 'Dashboard',
        url: WORKERS_DASHBOARD_PATH,
        permissions: [READ_EMPLOYEE_DASHBOARD],
        exact: true,
        isPageMenu: true,
      },
      {
        name: 'Workers',
        exceptionUrls: [EMPLOYEE_PORTAL_PATH],
        url: WORKERS_PATH,
        permissions: [GET_EMPLOYEES_LIST],
        isPageMenu: true,
      },
      positionProfilesEnabled && {
        name: 'Position profiles',
        url: POSITION_PROFILES_PATH,
        isAlpha: true,
        permissions: [POSITION_PROFILE_LIST_READ],
        isPageMenu: true,
      },
      positionSlotsEnabled && {
        name: 'Positions',
        url: POSITION_SLOTS_PATH,
        isAlpha: true,
        permissions: [POSITION_SLOT_LIST_READ],
        isPageMenu: true,
      },
      workforceAnalyticsEnabled && {
        name: 'Position analytics',
        url: WORKFORCE_ANALYTICS_PATH,
        isAlpha: true,
        permissions: [POSITION_SLOT_LIST_READ],
        isPageMenu: true,
      },
      {
        name: 'Leaves',
        url: '/leaves',
        permissions: [LEAVES_LIST_SEARCH],
        isPageMenu: true,
      },
      {
        name: 'Qualification structure',
        url: QUALIFICATION_STRUCTURE_PATH,
        permissions: [GET_EMPLOYEES_LIST, QUALIFICATION_STRUCTURE_LIST_READ],
        isPageMenu: true,
      },

      {
        name: 'Reports',
        isPageMenu: true,
        shouldUseCurrentName: true,
        exceptionUrls: [EMPLOYEE_PORTAL_PATH],
        url: WORKERS_PATH,
        permissions: [GET_ALL_CONTRACTS_LIST, PERFORMANCE_MEASUREMENT_GET, READ_PERFORMANCE_REPORT],
        children: asNavigationChildren([
          {
            name: 'Worker score',
            url: EMPLOYEE_SCORE_PATH,
            permissions: [PERFORMANCE_MEASUREMENT_GET],
            isPageMenu: true,
          },
          {
            name: 'Measurement and ranking',
            url: MEASUREMENT_PATH,
            permissions: [PERFORMANCE_MEASUREMENT_GET],
            isPageMenu: true,
          },

          {
            name: 'Performance report',
            url: PERFORMANCE_REPORTS_PATH,
            isPageMenu: true,
            exact: true,
            permissions: [READ_PERFORMANCE_REPORT],
          },
        ]),
      },

      {
        name: 'Skill based queues',
        url: SKILL_BASED_QUEUE_PATH,
        permissions: [READ_SKILL_BASED_QUEUES_LIST],
        isPageMenu: true,
      },
    ]),
  },
];

const team = [
  {
    name: 'Teams',
    url: TEAMS_PATH,
    shouldUseCurrentName: true,
    icon: <PeopleIcon />,
    permissions: [VIEW_ASSIGNMENTS_TAB, VIEW_STAFFING_DASHBOARD, CREATE_ASSIGNMENT],
    exceptionUrls: [WORKERS_PATH],
    children: asNavigationChildren([
      teamEnabled && {
        name: 'Dashboard',
        url: STAFFING_DASHBOARD_PATH,
        permissions: [VIEW_STAFFING_DASHBOARD],
        isPageMenu: true,
        children: asNavigationChildren([
          teamEnabled && {
            name: 'Projects',
            url: STAFFING_DASHBOARD_PATH,
            permissions: [VIEW_STAFFING_DASHBOARD],
            isPageMenu: true,
          },
          oppDashboardEnabled && {
            name: 'Opportunities',
            url: OPPORTUNITY_DASHBOARD_PATH,
            permissions: [VIEW_STAFFING_DASHBOARD],
            isPageMenu: true,
          },
        ]),
      },
      assignmentSidebarEnabled && {
        name: 'Assignments',
        url: ASSIGNMENT_PATH,
        permissions: [VIEW_ASSIGNMENTS_TAB],
        isPageMenu: true,
      },
      {
        name: 'Bench',
        url: BENCH_PATH,
        permissions: [CREATE_ASSIGNMENT],
        isPageMenu: true,
      },
    ]),
  },
];

const projectManagement = [
  {
    name: 'Projects',
    url: NEW_PROJECT_PATH,
    shouldUseCurrentName: true,
    icon: <ProjectsIcon />,
    exceptionUrls: [PROJECTS_COMPLIACNE],
    permissions: ['DELIVERY_PROJECT_READ_LIST'],
    children: asNavigationChildren([
      {
        isPageMenu: true,
        name: 'Dashboard',
        url: `${NEW_PROJECT_PATH}`,
        permissions: [READ_LIST],
        children: asNavigationChildren([
          projectDashboardEnabled && {
            name: 'Projects',
            url: `${NEW_PROJECT_PATH}/dashboard`,
            permissions: [READ_LIST],
            isPageMenu: true,
          },
          {
            name: 'Projects – Legacy View',
            url: getProjectListUrl(),
            permissions: [READ_PROJECTS_LIST],
            isPageMenu: true,
          },
        ]),
      },
    ]),
  },
];

const services = [
  {
    name: 'Services',
    url: SERVICE_CATALOG_PATH,
    shouldUseCurrentName: true,
    icon: <ServicesIcon />,
    permissions: [READ_SERVICE_CATALOG_LIST],
    children: asNavigationChildren([
      {
        isPageMenu: true,
        name: 'Dashboard',
        url: SERVICE_CATALOG_PATH,
        permissions: [READ_SERVICE_CATALOG_LIST],
      },
    ]),
  },
];

const suppliers = [
  {
    name: 'Suppliers',
    url: INVOICE_PATH,
    shouldUseCurrentName: true,
    icon: <SuppliersIcon />,
    permissions: [READ_INVOICE_LIST, FINANCE_VENDOR_LIST_READ, READ_PURCHASE_ORDER_LIST],
    children: asNavigationChildren([
      {
        name: 'Vendors',
        url: getVendorsListUrl(),
        isPageMenu: true,
        permissions: [FINANCE_VENDOR_LIST_READ],
      },
      {
        name: 'Invoices',
        url: INVOICE_PATH,
        isPageMenu: true,
        permissions: [READ_INVOICE_LIST],
      },
      {
        name: 'Purchase Orders',
        url: PURCHASE_ORDER_PATH,
        isPageMenu: true,
        permissions: [READ_PURCHASE_ORDER_LIST],
      },
    ]),
  },
];
// Have delete items in children
const finance = [
  {
    name: 'Finance',
    url: PEOPLE_PATH,
    shouldUseCurrentName: true,
    icon: <BudgetAccountingIcon />,
    permissions: [
      READ_PAYROLL_REPORT,
      READ_PAYROLL_REPORTS,
      READ_WORKING_HOURS_REPORT,
      READ_COST_CENTER_REPORT,
      GET_ACCOUNTING_CYCLE,
      READ_LEDGER_LIST,
      READ_JOURNAL_ENTRY,
      READ_EXPENSE_LIST,
    ],
    children: asNavigationChildren([
      {
        name: 'Reports',
        url: '/reports',
        shouldUseCurrentName: true,
        permissions: [READ_PAYROLL_REPORT, READ_WORKING_HOURS_REPORT, READ_PAYROLL_REPORTS, READ_COST_CENTER_REPORT],
        isPageMenu: true,
        children: asNavigationChildren([
          {
            name: 'Report',
            url: getPayrollListUrl(),
            exact: true,
            permissions: [READ_PAYROLL_REPORT],
            isPageMenu: true,
          },
          {
            name: 'Payroll Reports',
            url: getGeneratedPayrollsListUrl(),
            exact: true,
            permissions: [READ_PAYROLL_REPORTS],
            isPageMenu: true,
          },
          {
            name: 'Working Hours',
            url: getWorkingHoursListUrl(),
            permissions: [READ_WORKING_HOURS_REPORT],
            isPageMenu: true,
          },
          {
            name: 'Metrics',
            url: getCostAccountingListUrl(),
            permissions: [READ_COST_CENTER_REPORT],
            isPageMenu: true,
          },
        ]),
      },
      {
        name: 'Accounting Cycles',
        url: getAccountingCycleListUrl(),
        permissions: [GET_ACCOUNTING_CYCLE],
        isPageMenu: true,
        children: asNavigationChildren([]),
      },

      {
        name: 'Transactions',
        url: getLedgerListUrl(),
        permissions: [READ_LEDGER_LIST],
        isPageMenu: true,
        children: asNavigationChildren([
          {
            name: 'Ledger',
            url: getLedgerListUrl(),
            permissions: [READ_LEDGER_LIST],
            isPageMenu: true,
          },
          {
            name: 'Journal Entries',
            url: getJournalEntryListUrl(),
            permissions: [READ_JOURNAL_ENTRY],
            isPageMenu: true,
          },
        ]),
      },

      {
        name: 'Worker Costs',
        url: getExpenseListUrl(),
        permissions: [READ_EXPENSE_LIST],
        isPageMenu: true,
      },
    ]),
  },
];

const inventory = [
  {
    name: 'Inventory',
    url: ASSET_PATH,
    shouldUseCurrentName: true,
    icon: <InventoryIcon />,
    permissions: [READ_ASSET_LIST],
    exact: true,
    children: asNavigationChildren([
      {
        name: 'Assets',
        url: ASSET_PATH,
        isPageMenu: true,
        permissions: [READ_ASSET_LIST],
        exact: true,
      },
    ]),
  },
];
// Have delete items in children
const sales = [
  {
    name: 'Sales',
    url: CRM_PATH,
    isGeneral: true,
    shouldUseCurrentName: true,
    icon: <CrmIcon />,
    permissions: [READ_LEADS_LIST, READ_OPPORTUNITIES_LIST, READ_ACCOUNTS_LIST, READ_CONTACTS_LIST],
    children: asNavigationChildren([
      {
        name: 'Leads',
        url: LEADS_PATH,
        shouldUseCurrentName: true,
        permissions: [READ_LEADS_LIST],
        isPageMenu: true,
        children: asNavigationChildren([
          {
            name: 'Leads',
            url: LEADS_PATH,
            permissions: [READ_LEADS_LIST],
            isPageMenu: true,
          },
          {
            name: 'Reports',
            url: LEAD_REPORT_PATH,
            permissions: [LEAD_REPORT_PERMISSION],
            isPageMenu: true,
          },
        ]),
      },
      {
        name: 'Opportunities',
        url: OPPORTUNITY_PATH,
        isPageMenu: true,
        permissions: [READ_OPPORTUNITIES_LIST],
      },
      {
        name: 'Accounts',
        url: ACCOUNT_PATH,
        isPageMenu: true,
        permissions: [READ_ACCOUNTS_LIST],
      },
      {
        name: 'Contacts',
        url: CONTACTS_PATH,
        isPageMenu: true,
        permissions: [READ_CONTACTS_LIST],
      },
    ]),
  },
];

const securityCompliance = [
  {
    name: 'Security & Compliance',
    url: '/',
    icon: <GppGoodOutlinedIcon />,
    shouldUseCurrentName: true,
    permissions: [READ_SECURITY_AUDIT_LOG, UPLOAD_COMPLIANCE_REGISTRY_DOCUMENTS, PROJECTS_COMPLIANCE_LIST],
    children: asNavigationChildren([
      {
        name: 'Reports',
        url: COMPLIANCE_REPORT_PATH,
        permissions: [READ_SECURITY_AUDIT_LOG, UPLOAD_COMPLIANCE_REGISTRY_DOCUMENTS],
        shouldUseCurrentName: true,
        isPageMenu: true,
        children: asNavigationChildren([
          {
            name: 'Compliance Report',
            url: COMPLIANCE_REPORT_PATH,
            permissions: [UPLOAD_COMPLIANCE_REGISTRY_DOCUMENTS],
            isPageMenu: true,
          },
          {
            name: 'Roles',
            url: ROLES_AUDIT_PATH,
            permissions: [READ_SECURITY_AUDIT_LOG],
            isPageMenu: true,
          },
          {
            name: 'Permissions',
            url: PERMISSIONS_AUDIT_PATH,
            permissions: [READ_SECURITY_AUDIT_LOG],
            isPageMenu: true,
          },
          {
            name: 'Artifacts',
            url: ARTIFACTS_AUDIT_PATH,
            permissions: [READ_SECURITY_AUDIT_LOG],
            isPageMenu: true,
          },
        ]),
      },
      {
        name: 'Compliance Registry',
        url: COMPLIANCE_REGISTRY_PATH,
        permissions: [UPLOAD_COMPLIANCE_REGISTRY_DOCUMENTS],
        isBeta: true,
        isPageMenu: true,
      },
      projectsComplianceEnabled && {
        name: 'Projects compliance',
        url: PROJECTS_COMPLIACNE,
        permissions: [PROJECTS_COMPLIANCE_LIST],
        isPageMenu: true,
      },
    ]),
  },
];

const processes = [
  {
    name: 'Processes',
    url: PROCESS_INSTANCE_PATH,
    shouldUseCurrentName: true,
    icon: <ProcessesIcon />,
    permissions: [READ_DEFINITIONS_LIST, READ_INSTANCES_LIST, READ_PROCESS_ASSETS],
    children: asNavigationChildren([
      {
        name: 'Process Instances',
        url: PROCESS_INSTANCE_PATH,
        permissions: [READ_INSTANCES_LIST],
        isPageMenu: true,
      },
      operationalAssetsEnabled && {
        name: 'Operational Process Assets',
        url: PROCESS_ASSETS_PATH,
        permissions: [READ_PROCESS_ASSETS],
        isPageMenu: true,
      },
      {
        name: 'Process Definitions',
        url: PROCESS_DEFINITION_PATH,
        permissions: [READ_DEFINITIONS_LIST],
        isPageMenu: true,
      },
      taskConstructorEnabled && {
        name: 'Task Constructor',
        url: TASK_CONSTRUCTOR_PATH,
        permissions: [TASK_CONSTRUCTOR_LIST_READ],
        isPageMenu: true,
      },
    ]),
  },
];

const navigationConfig: { items: NavigationItemType[] } = {
  items: [
    ...syberryToday,
    ...artifactStorage,
    ...organization,
    ...recruitment,
    ...people,
    ...team,
    ...projectManagement,
    ...services,
    ...suppliers,
    ...finance,
    ...inventory,
    ...sales,
    ...securityCompliance,
    ...processes,

    ...oneClick,
  ],
};

export default navigationConfig;
